import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";

export interface ScrollShadowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "orientation">, VariantProps<typeof scrollShadowVariants> {
  size?: number;
  offset?: number;
  isEnabled?: boolean;
  onVisibilityChange?: (visibility: { top: boolean; bottom: boolean; left: boolean; right: boolean }) => void;
}

export const ScrollShadow = React.forwardRef<HTMLDivElement, ScrollShadowProps>(
  (
    {
      children,
      className,
      orientation = "vertical",
      hideScrollBar = false,
      size = 40,
      offset = 0,
      isEnabled = true,
      onVisibilityChange,
      ...rest
    },
    ref,
  ) => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [shadowState, setShadowState] = React.useState({
      top: false,
      bottom: false,
      left: false,
      right: false,
    });

    // Combine refs
    React.useImperativeHandle(ref, () => scrollRef.current!);

    const checkScrollPosition = React.useCallback(() => {
      if (!scrollRef.current || !isEnabled) return;

      const element = scrollRef.current;
      const isVertical = orientation === "vertical";

      if (isVertical) {
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        const hasOverflow = scrollHeight > clientHeight;
        const isScrolledFromTop = scrollTop > offset;
        const isScrolledToBottom = scrollTop + clientHeight >= scrollHeight - offset;

        const newState = {
          top: hasOverflow && isScrolledFromTop,
          bottom: hasOverflow && !isScrolledToBottom,
          left: false,
          right: false,
        };

        setShadowState((prev) => {
          if (prev.top !== newState.top || prev.bottom !== newState.bottom) {
            onVisibilityChange?.(newState);
            return newState;
          }
          return prev;
        });
      } else {
        const scrollLeft = element.scrollLeft;
        const scrollWidth = element.scrollWidth;
        const clientWidth = element.clientWidth;

        const hasOverflow = scrollWidth > clientWidth;
        const isScrolledFromLeft = scrollLeft > offset;
        const isScrolledToRight = scrollLeft + clientWidth >= scrollWidth - offset;

        const newState = {
          top: false,
          bottom: false,
          left: hasOverflow && isScrolledFromLeft,
          right: hasOverflow && !isScrolledToRight,
        };

        setShadowState((prev) => {
          if (prev.left !== newState.left || prev.right !== newState.right) {
            onVisibilityChange?.(newState);
            return newState;
          }
          return prev;
        });
      }
    }, [orientation, offset, isEnabled, onVisibilityChange]);

    // Handle scroll events
    React.useEffect(() => {
      const element = scrollRef.current;
      if (!element || !isEnabled) return;

      checkScrollPosition();

      element.addEventListener("scroll", checkScrollPosition, { passive: true });

      return () => {
        element.removeEventListener("scroll", checkScrollPosition);
      };
    }, [checkScrollPosition, isEnabled]);

    // Handle resize and content changes
    React.useEffect(() => {
      const element = scrollRef.current;
      if (!element || !isEnabled) return;

      const resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });

      resizeObserver.observe(element);

      // Also observe children for content changes
      const mutationObserver = new MutationObserver(() => {
        checkScrollPosition();
      });

      mutationObserver.observe(element, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }, [checkScrollPosition, isEnabled]);

    // Determine scroll state for data attributes
    const isVertical = orientation === "vertical";
    const topScroll = isVertical && shadowState.top && !shadowState.bottom;
    const bottomScroll = isVertical && shadowState.bottom && !shadowState.top;
    const topBottomScroll = isVertical && shadowState.top && shadowState.bottom;
    const leftScroll = !isVertical && shadowState.left && !shadowState.right;
    const rightScroll = !isVertical && shadowState.right && !shadowState.left;
    const leftRightScroll = !isVertical && shadowState.left && shadowState.right;

    return (
      <div
        ref={scrollRef}
        className={scrollShadowVariants({ orientation, hideScrollBar, className })}
        data-orientation={orientation}
        data-top-scroll={topScroll}
        data-bottom-scroll={bottomScroll}
        data-top-bottom-scroll={topBottomScroll}
        data-left-scroll={leftScroll}
        data-right-scroll={rightScroll}
        data-left-right-scroll={leftRightScroll}
        style={
          {
            "--scroll-shadow-size": `${size}px`,
          } as React.CSSProperties
        }
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ScrollShadow.displayName = "ScrollShadow";

const scrollShadowVariants = tv({
  base: [],
  variants: {
    orientation: {
      vertical: [
        "overflow-y-auto",
        "data-[top-scroll=true]:[mask-image:linear-gradient(0deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
        "data-[bottom-scroll=true]:[mask-image:linear-gradient(180deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
        "data-[top-bottom-scroll=true]:[mask-image:linear-gradient(#000,#000,transparent_0,#000_var(--scroll-shadow-size),#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
      ],
      horizontal: [
        "overflow-x-auto",
        "data-[left-scroll=true]:[mask-image:linear-gradient(270deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
        "data-[right-scroll=true]:[mask-image:linear-gradient(90deg,#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
        "data-[left-right-scroll=true]:[mask-image:linear-gradient(to_right,#000,#000,transparent_0,#000_var(--scroll-shadow-size),#000_calc(100%_-_var(--scroll-shadow-size)),transparent)]",
      ],
    },
    hideScrollBar: {
      true: "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
      false: "",
    },
  },
  defaultVariants: {
    orientation: "vertical",
    hideScrollBar: false,
  },
});
